import { format, subDays } from "date-fns"
import React, { useCallback, useMemo, useState } from "react"
import { ReportFilters } from "../../component/filters/report-filters.tsx"
import { Column, ReactTailwindTable, useTable } from "../../component/react-tailwind-table"
import { useFilters } from "../../component/use-filter"
import { useGetOperatorFactTrendsQuery } from "./service.ts"
import { useGetOperatorsQuery } from "../operator-report/service.ts"
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { groupBy } from "lodash"
import { ChatBubbleLeftEllipsisIcon, XMarkIcon } from "@heroicons/react/24/outline"
import "../../component/generic-components.css"
import { useSelection } from "../../app/department-context.tsx"

const getColor = (delta: number) => {
  if (delta > 0.5) return "#d35f26"
  if (delta > 0.2) return "#ffc658"
  return "#82ca9d"
}
const FactTrendsChart = ({ data }: { data: any[] }) => {
  const chartData = data.map(item => ({
    name: item.fact_title,
    delta: Math.round(item.delta * 100),
  }))

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={chartData} layout="vertical">
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis type="number" />
        <YAxis type="category" dataKey="name" width={400} />
        <Tooltip />
        <Legend />
        <Bar dataKey="delta">
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={getColor(entry.delta)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

const FactTrendsSection = ({ title, data, isFetching }: { title: string; data: any[]; isFetching: boolean }) => {
  const table = useTable({ defaultPageSize: 10 })
  const [expandedRows, setExpandedRows] = useState<string[]>([])

  const toggleRow = useCallback((rowId: string) => {
    setExpandedRows(prev => (prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId]))
  }, [])
  const columns: Column<any>[] = useMemo(
    () => [
      {
        id: "expander",
        accessorKey: "expander",
        size: 30,
        header: () => null,
        enableSorting: false,
        cell: ({ row }) => {
          const isExpanded = expandedRows.includes(row.id)
          return (
            <button
              onClick={() => toggleRow(row.id)}
              className={"cursor-pointer"}
              key={`expander-${row.id}-${isExpanded}`}
            >
              {isExpanded ? (
                <XMarkIcon width={24} height={24} />
              ) : (
                <ChatBubbleLeftEllipsisIcon width={24} height={24} />
              )}
            </button>
          )
        },
      },
      {
        header: "Факт",
        accessorKey: "fact_title",
      },
      {
        header: "Лучшие",
        accessorKey: "top_fact_rate_all",
        cell: ({ row }) => `${Math.round(Number(row.original.top_fact_rate_all) * 100)}%`,
        size: 70,
      },
      {
        header: "Среднее",
        accessorKey: "avg_fact_rate",
        cell: ({ row }) => `${Math.round(Number(row.original.avg_fact_rate) * 100)}%`,
        size: 70,
      },
      {
        header: "Текущее",
        accessorKey: "bottom_fact_rate",
        cell: ({ row }) => {
          const color = getColor(row.original.delta)
          return (
            <div className={"flex flex-row items-center gap-2"}>
              <div className={"w-2 h-2 rounded-lg"} style={{ backgroundColor: color }} />
              <span>{Math.round(Number(row.original.bottom_fact_rate) * 100)}%</span>
            </div>
          )
        },
        size: 70,
      },
    ],
    [expandedRows, toggleRow],
  )

  return (
    <div className="mb-8 border-b-teal-500">
      <h2 className="text-lg sm:text-xl font-bold text-teal-700 mb-4">
        Тип звонка <span className={"capitalize"}>"{title}"</span>
      </h2>
      <div className="flex flex-col gap-8">
        <div>
          <ReactTailwindTable
            columns={columns}
            query={() => ({
              data: {
                results: data,
              },
              isFetching: isFetching,
            })}
            {...table}
            visibilityOptions={{
              disablePagination: true,
            }}
            renderRowSubComponent={({ row }) => (
              <tr>
                <td colSpan={columns.length}>
                  <div className={"flex flex-col gap-4 items-stretch content-stretch m-4"}>
                    <b className={"ms-4"}>Реплики в лучших звонках</b>
                    {row.original.fact_references.map(fr => {
                      return (
                        <blockquote className={"alert text-xs p-4 ms-4 me-4"} key={fr}>
                          {fr}
                        </blockquote>
                      )
                    })}
                  </div>
                </td>
              </tr>
            )}
            expandedRows={expandedRows}
            tableId={`factTrendsTable-${title}`}
          />
        </div>
      </div>
      <hr className={"mb-8 mt-8 bg-teal-500 border-teal-500"} />
    </div>
  )
}

export const FactTrendsReport = () => {
  const { selectedDepartment } = useSelection()
  const { data: operators } = useGetOperatorsQuery({
    departmentId: selectedDepartment?.id,
  })

  const { filters, updateFilters } = useFilters({
    params: {
      startDate: "string",
      endDate: "string",
      departmentId: "number",
      operatorId: "number",
    },
    defaults: {
      departmentId: selectedDepartment?.id,
      startDate: format(subDays(new Date(), 30), "yyyy-MM-dd"),
      endDate: format(new Date(), "yyyy-MM-dd"),
      operatorId: operators?.[0]?.id,
    },
  })

  const { data: trends, isFetching } = useGetOperatorFactTrendsQuery({ ...filters })

  const groupedTrends = useMemo(() => {
    if (!trends) return {}
    return groupBy(trends.results, "call_type")
  }, [trends])

  return (
    <div>
      <div className="max-w-7xl mx-auto bg-white dark:bg-base-200  p-6 sm:p-8 rounded-lg shadow-sm">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-teal-800 mb-2">Тренды обучения</h1>

        <ReportFilters filters={filters} updateFilters={updateFilters} showOperatorFilter />
        {isFetching && (
          <div className={"flex justify-center"}>
            <span className="loading loading-spinner loading-lg m-2"></span>
          </div>
        )}
        {Object.entries(groupedTrends).map(([callType, data]) => (
          <FactTrendsSection key={callType} title={data[0].call_type_title} data={data} isFetching={isFetching} />
        ))}
      </div>
    </div>
  )
}

export default FactTrendsReport
